import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { Validators } from "@angular/forms";
import { IDatabaseConnectionHolder, IDatabaseConnectionHolderWithFields } from "@coso-replicator/core";
import { FormText, ViewMode, FormGroup, FormBuilder } from "@coso/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  standalone:false,
  templateUrl: "./add-edit-database-connection.component.html"
})
export class AddEditDatabaseConnectionComponent implements OnInit, OnDestroy {
  @ViewChild("sqlLiteFilePathPopover", { static: true }) sqlLiteFilePathPopover: TemplateRef<any>;
  ViewMode = ViewMode;
  private unsubscribe = new Subject<void>();

  /** Initial state properties */
  viewMode: ViewMode;
  entity: IDatabaseConnectionHolderWithFields;
  errorMessage: string;
  saveCallback: (entity: IDatabaseConnectionHolderWithFields) => void;

  formText: FormText<IDatabaseConnectionHolderWithFields>;
  formGroup: FormGroup<IDatabaseConnectionHolderWithFields>;

  constructor(public activeModal: NgbActiveModal, private fb: FormBuilder) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
  }

  ngOnInit(): void {
    this.formGroup = this.fb.group<IDatabaseConnectionHolderWithFields>({
      isSqlServer: this.fb.control<boolean>(false),
      databaseConnectionHolder: this.fb.group<IDatabaseConnectionHolder>({
        name: this.fb.control<string>(undefined, [Validators.required, Validators.pattern(/^[a-zA-Z0-9\s_-]{1,}$/)]),
        originalName: this.fb.control<string>(undefined),
        connectionString: this.fb.control<string>(undefined),
        modified: this.fb.control<boolean>(true),
        provider: this.fb.control<string>(undefined)
      }),
      sqlLiteFilePath: this.fb.control<string>(undefined, [Validators.required]),
      serverName: this.fb.control<string>(undefined),
      catalog: this.fb.control<string>(undefined),
      windowsAuthentication: this.fb.control<boolean>(false),
      username: this.fb.control<string>(undefined),
      password: this.fb.control<string>(undefined),
      encryptConnection: this.fb.control<boolean>(true),
      trustServerCertificate: this.fb.control<boolean>(false)
    });
    this.formGroup.controls.isSqlServer.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (value) {
        this.formGroup.controls.sqlLiteFilePath.clearValidators();
        this.formGroup.controls.sqlLiteFilePath.updateValueAndValidity();

        this.formGroup.controls.serverName.setValidators(Validators.required);
        this.formGroup.controls.serverName.markAsUntouched();
        this.formGroup.controls.serverName.markAsPristine();
        this.formGroup.controls.serverName.updateValueAndValidity();
        this.formGroup.controls.catalog.setValidators(Validators.required);
        this.formGroup.controls.catalog.markAsUntouched();
        this.formGroup.controls.catalog.markAsPristine();
        this.formGroup.controls.catalog.updateValueAndValidity();
      } else {
        this.formGroup.controls.serverName.clearValidators();
        this.formGroup.controls.serverName.updateValueAndValidity();
        this.formGroup.controls.catalog.clearValidators();
        this.formGroup.controls.catalog.updateValueAndValidity();

        this.formGroup.controls.sqlLiteFilePath.setValidators(Validators.required);
        this.formGroup.controls.sqlLiteFilePath.markAsUntouched();
        this.formGroup.controls.sqlLiteFilePath.markAsPristine();
        this.formGroup.controls.sqlLiteFilePath.updateValueAndValidity();
      }
    });
    this.formGroup.controls.windowsAuthentication.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      if (this.formGroup.controls.isSqlServer.value) {
        if (value) {
          this.formGroup.controls.username.clearValidators();
          this.formGroup.controls.username.updateValueAndValidity();
          this.formGroup.controls.password.clearValidators();
          this.formGroup.controls.password.updateValueAndValidity();
        } else {
          this.formGroup.controls.username.setValidators(Validators.required);
          this.formGroup.controls.username.markAsUntouched();
          this.formGroup.controls.username.markAsPristine();
          this.formGroup.controls.username.updateValueAndValidity();
          this.formGroup.controls.password.setValidators(Validators.required);
          this.formGroup.controls.password.markAsUntouched();
          this.formGroup.controls.password.markAsPristine();
          this.formGroup.controls.password.updateValueAndValidity();
        }
      }
    });
    this.formText = {
      databaseConnectionHolder: {
        name: {
          label: "Name",
          popover: "The name is just a field which appears later in the list of connections. By giving the connection a meaningful name, it is easier to find it later in the list.",
          placeholder: "name",
          errors: {
            required: "The Name field is required",
            pattern: "The Name field only letters, numbers, hyphens, underscores and white spaces."
          },
          labelClass: "col-lg-3",
          inputContainerClass: "col-lg-7"
        }
      },
      serverName: {
        label: "Server name",
        popover: "The name of the SQL Server instance.",
        placeholder: "server name",
        errors: {
          required: "The Server name field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7"
      },
      catalog: {
        label: "Catalogue",
        popover: "The name of the database.",
        placeholder: "database",
        errors: {
          required: "The Catalogue field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7"
      },
      windowsAuthentication: {
        label: "Use Windows Authentication",
        labelClass: "col-lg-3"
      },
      username: {
        label: "Username",
        placeholder: "username",
        popover: "The SQL Server login account.",
        errors: {
          required: "The Username field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7"
      },
      password: {
        label: "Password",
        placeholder: "password",
        popover: "The password for the SQL Server account logging on.",
        errors: {
          required: "The Password field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7"
      },
      encryptConnection: {
        label: "Encrypt connection",
        popover: "Indicates whether SQL Server uses SSL encryption for all data sent between the client and server if the server has a certificate installed.",
        labelClass: "col-lg-3"
      },
      trustServerCertificate: {
        label: "Trust server certificate",
        popover: "Indicates whether the channel will be encrypted while bypassing walking the certificate chain to validate trust.",
        labelClass: "col-lg-3"
      },
      sqlLiteFilePath: {
        label: "Database file path",
        placeholder: "path",
        popover: this.sqlLiteFilePathPopover,
        errors: {
          required: "The Database file path field is required"
        },
        labelClass: "col-lg-3",
        inputContainerClass: "col-lg-7"
      }
    };
    if (this.entity) {
      this.formGroup.patchValue(this.entity);
      this.formGroup.updateValueAndValidity();
    }
  }

  save() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
    } else {
      this.saveCallback(this.formGroup.getRawValue());
    }
  }

  onSQLiteClick() {
    this.formGroup.controls.isSqlServer.setValue(false);
  }

  onSqlServerClick() {
    this.formGroup.controls.isSqlServer.setValue(true);
  }
}
